import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown, Check } from 'lucide-react';

export interface DateRangeValue {
  startDate: string; // 'YYYY-MM-DD'
  endDate: string;   // 'YYYY-MM-DD'
  presetKey?: string;
  label?: string;
}

interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
  isLoading?: boolean;
}

export const formatYMD = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseYMD = (s: string): Date => {
  const parts = s.split('-').map(Number);
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

export const formatDM = (s: string): string => {
  if (!s) return '';
  const parts = s.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
  return s;
};

export const formatDMY = (s: string): string => {
  if (!s) return '';
  const parts = s.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return s;
};

const getPresetRanges = (today: Date = new Date()) => {
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();

  const todayStr = formatYMD(today);
  const yesterdayStr = formatYMD(new Date(y, m, d - 1));
  const last7Str = formatYMD(new Date(y, m, d - 6));
  const last14Str = formatYMD(new Date(y, m, d - 13));
  const last30Str = formatYMD(new Date(y, m, d - 29));
  const last90Str = formatYMD(new Date(y, m, d - 89));

  const thisMonthStart = formatYMD(new Date(y, m, 1));
  const thisMonthEnd = todayStr;

  const lastMonthStart = formatYMD(new Date(y, m - 1, 1));
  const lastMonthEnd = formatYMD(new Date(y, m, 0));

  const thisYearStart = formatYMD(new Date(y, 0, 1));
  const thisYearEnd = todayStr;

  const lastYearStart = formatYMD(new Date(y - 1, 0, 1));
  const lastYearEnd = formatYMD(new Date(y - 1, 11, 31));

  const allTimeStart = formatYMD(new Date(y - 1, m, d));
  const allTimeEnd = todayStr;

  return [
    { key: 'today', label: 'Hôm nay', startDate: todayStr, endDate: todayStr },
    { key: 'yesterday', label: 'Hôm qua', startDate: yesterdayStr, endDate: yesterdayStr },
    { key: '7_days', label: '7 ngày qua', startDate: last7Str, endDate: todayStr },
    { key: '14_days', label: '14 ngày qua', startDate: last14Str, endDate: todayStr },
    { key: '30_days', label: '30 ngày qua', startDate: last30Str, endDate: todayStr },
    { key: '90_days', label: '90 ngày qua', startDate: last90Str, endDate: todayStr },
    { key: 'this_month', label: 'Tháng này', startDate: thisMonthStart, endDate: thisMonthEnd },
    { key: 'last_month', label: 'Tháng trước', startDate: lastMonthStart, endDate: lastMonthEnd },
    { key: 'this_year', label: 'Năm nay', startDate: thisYearStart, endDate: thisYearEnd },
    { key: 'last_year', label: 'Năm trước', startDate: lastYearStart, endDate: lastYearEnd },
    { key: 'all_time', label: 'Tất cả', startDate: allTimeStart, endDate: allTimeEnd },
  ];
};

const MONTH_NAMES = [
  'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4',
  'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8',
  'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'
];

const WEEKDAY_NAMES = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Temporary selection inside popover before clicking "Áp dụng"
  const [tempStart, setTempStart] = useState<string>(value.startDate);
  const [tempEnd, setTempEnd] = useState<string>(value.endDate);
  const [tempPreset, setTempPreset] = useState<string | undefined>(value.presetKey || '7_days');
  const [selectingStep, setSelectingStep] = useState<'idle' | 'start_selected'>('idle');
  const [hoverDate, setHoverDate] = useState<string | null>(null);

  // Calendar month views: viewDate points to the first month shown (left month)
  const [viewDate, setViewDate] = useState<Date>(() => {
    const end = parseYMD(value.endDate || formatYMD(new Date()));
    // Show previous month on left, current month on right
    return new Date(end.getFullYear(), end.getMonth() - 1, 1);
  });

  const presets = getPresetRanges();

  // Synchronize when value changes from outside
  useEffect(() => {
    setTempStart(value.startDate);
    setTempEnd(value.endDate);
    setTempPreset(value.presetKey);
  }, [value]);

  // Handle click outside to close popover
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        // Reset temp state
        setTempStart(value.startDate);
        setTempEnd(value.endDate);
        setTempPreset(value.presetKey);
        setSelectingStep('idle');
        setHoverDate(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, value]);

  // Navigate month views
  const handlePrevMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Handle Preset selection
  const handleSelectPreset = (preset: typeof presets[0]) => {
    setTempStart(preset.startDate);
    setTempEnd(preset.endDate);
    setTempPreset(preset.key);
    setSelectingStep('idle');
    setHoverDate(null);

    // Center view on preset dates
    const end = parseYMD(preset.endDate);
    setViewDate(new Date(end.getFullYear(), end.getMonth() - 1, 1));
  };

  // Handle Date Click on calendar
  const handleDateClick = (dateStr: string) => {
    if (selectingStep === 'idle') {
      // First click: select start date
      setTempStart(dateStr);
      setTempEnd(dateStr);
      setSelectingStep('start_selected');
      setTempPreset(undefined);
    } else {
      // Second click: select end date
      let newStart = tempStart;
      let newEnd = dateStr;
      if (newEnd < newStart) {
        // Swap if end is earlier than start
        const t = newStart;
        newStart = newEnd;
        newEnd = t;
      }
      setTempStart(newStart);
      setTempEnd(newEnd);
      setSelectingStep('idle');
      setHoverDate(null);

      // Check if matches any preset
      const matched = presets.find(p => p.startDate === newStart && p.endDate === newEnd);
      setTempPreset(matched ? matched.key : undefined);
    }
  };

  // Handle Apply button
  const handleApply = () => {
    let finalStart = tempStart;
    let finalEnd = tempEnd;
    if (finalEnd < finalStart) {
      const t = finalStart;
      finalStart = finalEnd;
      finalEnd = t;
    }

    const matched = presets.find(p => p.startDate === finalStart && p.endDate === finalEnd);
    let label = matched ? matched.label : `${formatDMY(finalStart)} – ${formatDMY(finalEnd)}`;

    onChange({
      startDate: finalStart,
      endDate: finalEnd,
      presetKey: matched ? matched.key : undefined,
      label
    });

    setIsOpen(false);
    setSelectingStep('idle');
    setHoverDate(null);
  };

  // Calculate day count
  const calculateDaysCount = (startStr: string, endStr: string) => {
    if (!startStr || !endStr) return 1;
    const start = parseYMD(startStr);
    const end = parseYMD(endStr);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  // Generate Month Grid (Monday-first: 1..7)
  const renderMonthGrid = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Monday is 0, Sunday is 6
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek === -1) startDayOfWeek = 6;

    // Previous month filler days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    const prevDays: Array<{ day: number; dateStr: string; isCurrentMonth: boolean }> = [];
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = prevMonthLastDay - i;
      const prevDate = new Date(year, month - 1, d);
      prevDays.push({
        day: d,
        dateStr: formatYMD(prevDate),
        isCurrentMonth: false,
      });
    }

    // Current month days
    const currentDays: Array<{ day: number; dateStr: string; isCurrentMonth: boolean }> = [];
    for (let i = 1; i <= daysInMonth; i++) {
      const currDate = new Date(year, month, i);
      currentDays.push({
        day: i,
        dateStr: formatYMD(currDate),
        isCurrentMonth: true,
      });
    }

    // Next month filler days (fill up to 42 cells = 6 rows)
    const totalFilled = prevDays.length + currentDays.length;
    const nextDaysCount = (totalFilled > 35 ? 42 : 35) - totalFilled;
    const nextDays: Array<{ day: number; dateStr: string; isCurrentMonth: boolean }> = [];
    for (let i = 1; i <= nextDaysCount; i++) {
      const nextDate = new Date(year, month + 1, i);
      nextDays.push({
        day: i,
        dateStr: formatYMD(nextDate),
        isCurrentMonth: false,
      });
    }

    const allCells = [...prevDays, ...currentDays, ...nextDays];

    // Determine effective range for highlighting
    let rangeStart = tempStart;
    let rangeEnd = tempEnd;
    if (selectingStep === 'start_selected' && hoverDate) {
      if (hoverDate < rangeStart) {
        rangeStart = hoverDate;
        rangeEnd = tempStart;
      } else {
        rangeEnd = hoverDate;
      }
    }

    return (
      <div className="w-full">
        <div className="grid grid-cols-7 gap-1 text-center mb-1">
          {WEEKDAY_NAMES.map((name, idx) => (
            <div key={idx} className="text-[11px] font-semibold text-slate-400 py-1">
              {name}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-y-1">
          {allCells.map((cell, idx) => {
            const isStart = cell.dateStr === tempStart;
            const isEnd = (selectingStep === 'idle' && cell.dateStr === tempEnd) || (selectingStep === 'start_selected' && cell.dateStr === hoverDate);
            const isBoth = isStart && isEnd;
            const inRange = cell.dateStr >= rangeStart && cell.dateStr <= rangeEnd;

            let cellClass = 'h-8 text-xs flex items-center justify-center transition-all cursor-pointer font-medium relative';
            let bgClass = '';
            let textClass = cell.isCurrentMonth ? 'text-slate-800' : 'text-slate-300 pointer-events-none';

            if (isBoth) {
              bgClass = 'bg-[#224783] text-white font-bold rounded-lg shadow-xs z-10';
              textClass = 'text-white';
            } else if (isStart) {
              bgClass = 'bg-[#224783] text-white font-bold rounded-l-lg shadow-xs z-10';
              textClass = 'text-white';
            } else if (isEnd) {
              bgClass = 'bg-[#224783] text-white font-bold rounded-r-lg shadow-xs z-10';
              textClass = 'text-white';
            } else if (inRange) {
              bgClass = 'bg-blue-50 text-[#224783] font-semibold';
              textClass = 'text-[#224783]';
            } else if (cell.isCurrentMonth) {
              bgClass = 'hover:bg-slate-100 rounded-lg';
            }

            return (
              <button
                key={idx}
                type="button"
                disabled={!cell.isCurrentMonth}
                onClick={() => cell.isCurrentMonth && handleDateClick(cell.dateStr)}
                onMouseEnter={() => {
                  if (selectingStep === 'start_selected' && cell.isCurrentMonth) {
                    setHoverDate(cell.dateStr);
                  }
                }}
                className={`${cellClass} ${bgClass} ${textClass}`}
              >
                {cell.day}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Month 1 and Month 2
  const m1Year = viewDate.getFullYear();
  const m1Month = viewDate.getMonth();
  const nextMonthDate = new Date(m1Year, m1Month + 1, 1);
  const m2Year = nextMonthDate.getFullYear();
  const m2Month = nextMonthDate.getMonth();

  // Active trigger button label
  const activePreset = presets.find(p => p.startDate === value.startDate && p.endDate === value.endDate);
  const triggerLabel = activePreset
    ? `${activePreset.label} (${formatDM(value.startDate)} - ${formatDM(value.endDate)})`
    : `${formatDMY(value.startDate)} – ${formatDMY(value.endDate)}`;

  const tempDaysCount = calculateDaysCount(tempStart, tempEnd);

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="inline-flex items-center space-x-2 px-3.5 py-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl text-xs font-bold text-slate-700 hover:text-slate-900 shadow-xs transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#224783]/20"
      >
        <Calendar className="w-4 h-4 text-[#224783] shrink-0" />
        <span className="truncate max-w-[210px] sm:max-w-none">{triggerLabel}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Popover Dropdown (Matches image.png structure) */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden w-[95vw] max-w-[760px] animate-in fade-in zoom-in-95 duration-150">
          <div className="flex flex-col md:flex-row">
            {/* Left Sidebar: Presets Menu */}
            <div className="w-full md:w-44 bg-slate-50/70 border-b md:border-b-0 md:border-r border-slate-200/80 p-3 shrink-0">
              <div className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider px-2 mb-1.5 hidden md:block">
                Mốc thời gian
              </div>
              <div className="grid grid-cols-2 md:grid-cols-1 gap-1 max-h-48 md:max-h-none overflow-y-auto">
                {presets.map(p => {
                  const isSelected = tempPreset === p.key || (tempStart === p.startDate && tempEnd === p.endDate);
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => handleSelectPreset(p)}
                      className={`text-left px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center justify-between ${
                        isSelected
                          ? 'bg-slate-200/90 text-[#224783] font-bold shadow-xs'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      <span>{p.label}</span>
                      {isSelected && <Check className="w-3.5 h-3.5 text-[#224783] shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right Side: Dual-Month Calendar */}
            <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between">
              <div>
                {/* Month Navigation & Month Headers */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-3 border-b border-slate-100">
                  {/* Month 1 Header */}
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handlePrevMonth}
                      className="p-1 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 cursor-pointer transition-colors"
                      title="Tháng trước"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs sm:text-sm font-bold text-slate-800">
                      {MONTH_NAMES[m1Month]} {m1Year}
                    </span>
                    <div className="w-4 sm:hidden" />
                  </div>

                  {/* Month 2 Header */}
                  <div className="hidden sm:flex items-center justify-between">
                    <div className="w-4" />
                    <span className="text-xs sm:text-sm font-bold text-slate-800">
                      {MONTH_NAMES[m2Month]} {m2Year}
                    </span>
                    <button
                      type="button"
                      onClick={handleNextMonth}
                      className="p-1 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 cursor-pointer transition-colors"
                      title="Tháng sau"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* 2 Month Grids */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-3">
                  <div>{renderMonthGrid(m1Year, m1Month)}</div>
                  <div className="hidden sm:block">{renderMonthGrid(m2Year, m2Month)}</div>
                </div>
              </div>

              {/* Bottom Footer Action Bar */}
              <div className="mt-4 pt-3 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs text-slate-500 font-medium text-center sm:text-left">
                  <span>Khoảng chọn: </span>
                  <span className="font-bold text-[#224783]">
                    {formatDMY(tempStart)} – {formatDMY(tempEnd)}
                  </span>
                  <span className="text-slate-400 ml-1.5">({tempDaysCount} ngày)</span>
                  {selectingStep === 'start_selected' && (
                    <span className="text-amber-600 font-semibold block text-[11px] mt-0.5 animate-pulse">
                      * Nhấp chọn ngày kết thúc
                    </span>
                  )}
                </div>

                <div className="flex items-center space-x-2 w-full sm:w-auto justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      setTempStart(value.startDate);
                      setTempEnd(value.endDate);
                      setTempPreset(value.presetKey);
                      setSelectingStep('idle');
                    }}
                    className="px-3.5 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl cursor-pointer transition-colors"
                  >
                    Hủy
                  </button>
                  <button
                    type="button"
                    onClick={handleApply}
                    className="flex-1 sm:flex-none px-5 py-2 bg-[#224783] hover:bg-[#1a3869] text-white text-xs sm:text-sm font-bold rounded-xl shadow-xs cursor-pointer transition-colors flex items-center justify-center space-x-1.5"
                  >
                    <span>Áp dụng</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
